import { useState } from 'react'
import dayjs from '@dcp/shared/utils/dayjs'
import { useFieldArray, useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Copy, Trash2, Plus, Loader2 } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import { useCreateProjections, useCategories } from '@/hooks/useProjections'
import { useFacilities } from '@/hooks/useBanking'
import { Modal, Button, Input, Label, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, DatePicker, MultiSelect } from '@atrvd/saas-ui'
import { getDefaultPriority } from '@/lib/projection-utils'
import { AmountInput } from './AmountInput'
import { projectionsFormSchema, type ProjectionsFormData } from './projectionBatchFormSchema'

/**
 * Props for ProjectionBatchFormDialog component
 */
interface ProjectionBatchFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Optional callback invoked with the IDs of newly created projections */
  onCreated?: (ids: string[]) => void
}

/**
 * Modal dialog for adding multiple projections at once
 * Provides inline form with ability to add/remove/copy rows
 */
export function ProjectionBatchFormDialog({ open, onOpenChange, onCreated }: ProjectionBatchFormDialogProps) {
  const createProjections = useCreateProjections()
  const { data: facilities = [], isLoading: facilitiesLoading } = useFacilities()
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories()
  const categories = categoriesData ?? []
  const isDataLoading = facilitiesLoading || categoriesLoading

  const [isSubmitting, setIsSubmitting] = useState(false)

  /**
   * Form setup with default empty row
   */
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ProjectionsFormData>({
    resolver: zodResolver(projectionsFormSchema),
    defaultValues: {
      projections: [
        {
          facility_ids: [],
          facility_amounts: [],
          category_id: '',
          date: dayjs().format('YYYY-MM-DD'),
          description: '',
          // DCP-255: undefined renders as empty placeholder in AmountInput (not 0),
          // so leaving the field blank triggers the "Amount is required" validation.
          amount: undefined,
          priority: false,
        },
      ],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'projections',
  })

  /**
   * Add new empty row
   */
  const handleAddNew = () => {
    append({
      facility_ids: [],
      facility_amounts: [],
      category_id: '',
      date: dayjs().format('YYYY-MM-DD'),
      description: '',
      // DCP-255: undefined so new rows start empty and trigger required validation.
      amount: undefined,
      priority: false,
    })
  }

  /**
   * Copy existing row including facility_ids and facility_amounts
   */
  const handleCopy = (index: number) => {
    const rowToCopy = watch(`projections.${index}`)
    append({
      ...rowToCopy,
      facility_ids: [...rowToCopy.facility_ids],
      facility_amounts: rowToCopy.facility_amounts.map((fa) => ({ ...fa })),
    })
  }

  /**
   * Remove row (if more than one exists)
   */
  const handleRemove = (index: number) => {
    if (fields.length > 1) {
      remove(index)
    } else {
      toast.error('At least one projection is required')
    }
  }

  /**
   * Handle facility selection change for a row.
   * Syncs the facility_amounts array: preserves existing amounts for
   * still-selected facilities, initializes new ones with the row's main amount.
   */
  const handleFacilityChange = (rowIndex: number, selectedIds: string[]) => {
    const currentAmounts = watch(`projections.${rowIndex}.facility_amounts`)
    const currentAmount = watch(`projections.${rowIndex}.amount`)

    const newFacilityAmounts = selectedIds.map((facilityId) => {
      const existing = currentAmounts.find((fa) => fa.facility_id === facilityId)
      return existing || { facility_id: facilityId, amount: currentAmount }
    })

    setValue(`projections.${rowIndex}.facility_ids`, selectedIds)
    setValue(`projections.${rowIndex}.facility_amounts`, newFacilityAmounts)
  }

  /**
   * Submit all projections.
   * Flattens multi-facility rows into individual projection payloads
   * so each facility gets its own projection record.
   */
  const onSubmit = async (data: ProjectionsFormData) => {
    setIsSubmitting(true)
    try {
      const projectionPayloads = data.projections.flatMap((projection) => {
        // DCP-250: single signed amount (positive=inflow, negative=outflow).
        // Multiple facilities with per-facility amounts
        if (projection.facility_ids.length > 1 && projection.facility_amounts.length > 0) {
          return projection.facility_amounts.map((fa) => ({
            category_id: projection.category_id,
            facility_id: fa.facility_id,
            date: projection.date,
            description: projection.description || undefined,
            // superRefine guarantees fa.amount is defined when facility_ids.length > 1
            amount: fa.amount!,
            status: 'draft' as const,
            // DCP-372: always false. The column exists and is NOT NULL, but
            // nothing reads it — no recurrence is ever generated — so the form
            // no longer offers the choice. Sent explicitly rather than omitted
            // so the row's shape stays obvious at the call site.
            is_recurring: false,
            priority: projection.priority ?? false,
          }))
        }

        // Single or no facility — use main amount
        return [{
          category_id: projection.category_id,
          facility_id: projection.facility_ids[0] || undefined,
          date: projection.date,
          description: projection.description || undefined,
          // superRefine guarantees projection.amount is defined when facility_ids.length <= 1
          amount: projection.amount!,
          status: 'draft' as const,
          // DCP-372: always false — see the note on the multi-facility branch.
          is_recurring: false,
          priority: projection.priority ?? false,
        }]
      })

      // Single batch insert — capture result to notify parent of created IDs
      const result = await createProjections.mutateAsync(projectionPayloads)
      if (onCreated && result) {
        onCreated(result.map((r: { id: string }) => r.id))
      }
      toast.success(`${projectionPayloads.length} projection${projectionPayloads.length > 1 ? 's' : ''} created successfully`)
      reset()
      onOpenChange(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create projections'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Discard and close
   */
  const handleDiscard = () => {
    reset()
    onOpenChange(false)
  }

  return (
    <Modal
      isOpen={open}
      onClose={handleDiscard}
      title="Add Projections"
      size="xl"
      className="!max-w-[1400px]"
      // DCP-332 (PResto 54821 #1): actions live in the Modal's `footer`, outside its
      // scroll container — one pattern across every form dialog. Kept CONDITIONAL on
      // `isDataLoading` so this is a pure relocation: while the reference data loads
      // the dialog shows a skeleton and no actions, exactly as before.
      footer={
        isDataLoading ? undefined : (
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleDiscard}>
              Discard
            </Button>
            <Button
              type="submit"
              form="batch-projection-form"
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isSubmitting ? 'Adding...' : 'Add projections'}
            </Button>
          </div>
        )
      }
    >
      <form id="batch-projection-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {isDataLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
        {/* DCP-250: amounts are signed — one note for the whole batch so each row
            doesn't need its own hint. Positive = inflow, negative = outflow. */}
        <p className="text-sm text-muted-foreground px-2">
          Amounts are signed: enter a positive value for inflow, a negative value for outflow (e.g. -500).
        </p>
        {/* No own max-height / overflow on this rows list (DCP-332, PResto 54821 #4):
            same as ManualTransactionBatchFormDialog, fixed in this PR — the Modal
            body already scrolls, so this produced a second, inner scrollbar once the
            batch had enough rows. Its max-h matched the modal's own 60vh, so it never
            bought any extra room either. */}
        <div className="space-y-4 px-2">
          {fields.map((field, index) => {
            const watchedFacilityIds = watch(`projections.${index}.facility_ids`)
            const watchedFacilityAmounts = watch(`projections.${index}.facility_amounts`)
            const isMultiFacility = watchedFacilityIds.length > 1

            return (
              <div key={field.id} className="px-4 pt-4 pb-10 border rounded-lg space-y-0">
                <div className="grid grid-cols-[minmax(150px,200px)_1fr_1fr_1fr_1fr_auto_auto] gap-4 items-end">
                  {/* Facility — multi-select with search */}
                  <div className="space-y-1.5">
                    {index === 0 && (
                      <Label className="text-xs font-medium bg-purple-100 text-purple-900 px-2 py-0.5 rounded inline-block">
                        Facility
                      </Label>
                    )}
                    {/* relative wrapper keeps the error out of flow so sibling cells stay aligned */}
                    <div className="relative">
                      <MultiSelect
                        options={facilities.map((f) => ({
                          value: f.id,
                          label: `${f.name}${f.code ? ` (${f.code})` : ''}`,
                        }))}
                        value={watchedFacilityIds}
                        onChange={(selectedIds) => handleFacilityChange(index, selectedIds)}
                        placeholder="Select facilities"
                        searchable
                        searchPlaceholder="Search facilities..."
                        className="max-w-[200px]"
                      />
                      {/* DCP-255: facility is required */}
                      {errors.projections?.[index]?.facility_ids && (
                        <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                          {errors.projections[index]?.facility_ids?.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Transaction Category */}
                  <div className="space-y-1.5">
                    {index === 0 && (
                      <Label htmlFor={`projections.${index}.category_id`} className="text-xs font-medium">
                        Transaction category
                      </Label>
                    )}
                    {/* relative wrapper keeps the error out of flow so sibling cells stay aligned */}
                    <div className="relative">
                      <Select
                        value={watch(`projections.${index}.category_id`)}
                        onValueChange={(value) => {
                          setValue(`projections.${index}.category_id`, value)
                          setValue(`projections.${index}.priority`, getDefaultPriority(categories, value))
                        }}
                      >
                        <SelectTrigger id={`projections.${index}.category_id`}>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {/* DCP-250: flat category list — sign comes from the amount, not category type */}
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.projections?.[index]?.category_id && (
                        <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                          {errors.projections[index]?.category_id?.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Projected Payment Date */}
                  <div className="space-y-1.5">
                    {index === 0 && (
                      <Label className="text-xs font-medium">
                        Projected Payment date
                      </Label>
                    )}
                    {/* relative wrapper keeps the error out of flow so sibling cells stay aligned */}
                    <div className="relative">
                      <DatePicker
                        date={watch(`projections.${index}.date`) ? new Date(watch(`projections.${index}.date`)) : undefined}
                        onDateChange={(date) => {
                          if (!date) {
                            setValue(`projections.${index}.date`, '')
                            return
                          }
                          setValue(`projections.${index}.date`, dayjs(date).format('YYYY-MM-DD'))
                        }}
                        placeholder="Select date"
                        className="w-full"
                      />
                      {errors.projections?.[index]?.date && (
                        <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">{errors.projections[index]?.date?.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-1.5">
                    {index === 0 && (
                      <Label htmlFor={`projections.${index}.description`} className="text-xs font-medium">
                        Description
                      </Label>
                    )}
                    {/* relative wrapper keeps the error out of flow so sibling cells stay aligned */}
                    <div className="relative">
                      <Input
                        id={`projections.${index}.description`}
                        placeholder="Type here"
                        {...register(`projections.${index}.description`)}
                      />
                      {/* DCP-255: description is required */}
                      {errors.projections?.[index]?.description && (
                        <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                          {errors.projections[index]?.description?.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Amount — DCP-224/225: locale-stable AmountInput; hidden when multi-facility */}
                  <div className="space-y-1.5">
                    {index === 0 && (
                      <Label htmlFor={`projections.${index}.amount`} className="text-xs font-medium">
                        Amount
                      </Label>
                    )}
                    {isMultiFacility ? (
                      <div className="h-9 flex items-center text-sm text-muted-foreground px-3 border rounded-md bg-muted/50">
                        Per-facility
                      </div>
                    ) : (
                      /* relative wrapper keeps the error out of flow so sibling cells stay aligned */
                      <div className="relative">
                        <Controller
                          name={`projections.${index}.amount`}
                          control={control}
                          render={({ field }) => (
                            <AmountInput
                              id={`projections.${index}.amount`}
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="0.00"
                            />
                          )}
                        />
                        {errors.projections?.[index]?.amount && (
                          <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">{errors.projections[index]?.amount?.message}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Priority — bottom-aligned via items-end on the grid.
                      "Make recurring" used to sit beside it and was removed
                      (DCP-372): nothing in the product acts on the flag, so the
                      checkbox promised a schedule that would never run. See the
                      note on `is_recurring` below. */}
                  <div className="flex items-center gap-4 shrink-0 pb-0.5">
                    <div className="flex items-center gap-2">
                      <input
                        id={`projections.${index}.priority`}
                        type="checkbox"
                        {...register(`projections.${index}.priority`)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor={`projections.${index}.priority`} className="text-sm font-normal cursor-pointer whitespace-nowrap">
                        Priority
                      </Label>
                    </div>
                  </div>

                  {/* Action Buttons — bottom-aligned via items-end on the grid */}
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(index)}
                      className="h-9 w-9 p-0"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(index)}
                      disabled={fields.length === 1}
                      className="h-9 w-9 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Per-facility amount sub-rows — DCP-224/225: AmountInput; shown when >1 facility */}
                {isMultiFacility && (
                  <div className="ml-8 space-y-2">
                    {watchedFacilityAmounts.map((fa, faIndex) => {
                      const facility = facilities.find((f) => f.id === fa.facility_id)
                      return (
                        <div key={fa.facility_id} className="flex items-center gap-4 pl-4 mt-3 border-l-2 border-primary/30">
                          <span className="text-sm text-muted-foreground w-[180px] truncate">
                            {facility?.name || 'Unknown'}
                          </span>
                          {/* DCP-231: this per-facility sub-list is a VERTICAL stack of
                              single-field rows (no side-by-side fields to keep aligned),
                              so the error stays IN FLOW — the row grows and pushes the
                              next facility row down (`space-y-2`/`mt-3`). Absolute here
                              would overlap the following row since no space is reserved. */}
                          <div>
                            <Controller
                              name={`projections.${index}.facility_amounts.${faIndex}.amount`}
                              control={control}
                              render={({ field }) => (
                                <AmountInput
                                  value={field.value}
                                  onChange={field.onChange}
                                  onBlur={field.onBlur}
                                  placeholder="0.00"
                                  className="w-[150px]"
                                />
                              )}
                            />
                            {/* DCP-224: surface validation errors for per-facility amounts
                                (e.g. invalid text → NaN) — matches the main amount field. */}
                            {errors.projections?.[index]?.facility_amounts?.[faIndex]?.amount && (
                              <p className="mt-0.5 text-xs text-destructive">
                                {errors.projections[index]?.facility_amounts?.[faIndex]?.amount?.message}
                              </p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}

          {/* Add New Button */}
          <Button type="button" variant="outline" onClick={handleAddNew} className="mt-4">
            <Plus className="h-4 w-4 mr-2" />
            Add new
          </Button>
        </div>
          </>
        )}
      </form>
    </Modal>
  )
}
